import React, { useState } from 'react';

interface Integration {
  id: string;
  name: string;
  category: string;
  status: 'Connected' | 'Not Connected' | 'Error';
  description: string;
  required: boolean;
  envKey: string;
  setupNote?: string;
}

const INTEGRATIONS: Integration[] = [
  {
    id: 'anthropic',
    name: 'Anthropic Claude',
    category: 'AI Engine',
    status: 'Connected',
    description: 'Powers all 13 Phoenix AI agents. Required for platform operation.',
    required: true,
    envKey: 'ANTHROPIC_API_KEY'
  },
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'Billing',
    status: 'Not Connected',
    description: 'Outcome-based billing, invoicing, monthly profit-share collection, and subscription management.',
    required: true,
    envKey: 'STRIPE_SECRET_KEY'
  },
  {
    id: 'proton_smtp',
    name: 'Proton Mail SMTP',
    category: 'Email',
    status: 'Not Connected',
    description: 'All transactional email: onboarding, Phoenix drip sequence, compliance reports, Agent 12 alerts. Uses SMTP Token — no Bridge required.',
    required: true,
    envKey: 'SMTP_USER',
    setupNote: 'Settings → All Settings → IMAP/SMTP → SMTP tokens → Generate token. Host: smtp.protonmail.ch  Port: 587'
  },
  {
    id: 'telnyx',
    name: 'Telnyx SMS',
    category: 'SMS',
    status: 'Not Connected',
    description: 'SMS escalation alerts to Agent 12, workflow trigger messages to leads and contacts.',
    required: true,
    envKey: 'TELNYX_API_KEY',
    setupNote: 'Telnyx portal → Messaging → Add profile → Purchase number. Set TELNYX_API_KEY and TELNYX_PHONE_NUMBER in .env'
  },
  {
    id: 'vapi',
    name: 'Vapi.ai Voice',
    category: 'Voice AI',
    status: 'Not Connected',
    description: 'AI voice agent outbound calls. Each Phoenix agent (Sarah, Mike, Emma, etc.) has its own Vapi assistant with pre-built scripts.',
    required: true,
    envKey: 'VAPI_API_KEY',
    setupNote: 'dashboard.vapi.ai → Assistants → create one per agent → copy Assistant IDs to VAPI_ASSISTANT_* env vars'
  },
  {
    id: 'docuseal',
    name: 'DocuSeal (Self-Hosted)',
    category: 'Signatures',
    status: 'Not Connected',
    description: 'Agent 12 one-tap policy binding. Legally valid electronic signatures (ESIGN / UETA compliant). Self-hosted on your server.',
    required: true,
    envKey: 'DOCUSEAL_API_KEY',
    setupNote: 'Docker-compose deploy DocuSeal on server → Settings → API → copy key. Set DOCUSEAL_BASE_URL (e.g. https://sign.lifeandwealthsolutions.online) and DOCUSEAL_WEBHOOK_SECRET'
  },
  {
    id: 'proton_drive',
    name: 'Proton Drive',
    category: 'Storage',
    status: 'Not Connected',
    description: 'Encrypted document storage for signed policies, compliance records, and agent reports.',
    required: false,
    envKey: 'PROTON_DRIVE_ENABLED'
  },
  {
    id: 'openai',
    name: 'OpenAI',
    category: 'AI Engine',
    status: 'Not Connected',
    description: 'ML model training, lead scoring embeddings, supplemental AI features.',
    required: false,
    envKey: 'OPENAI_API_KEY'
  },
  {
    id: 'paypal',
    name: 'PayPal',
    category: 'Billing',
    status: 'Not Connected',
    description: 'Alternative payment option for clients who prefer PayPal.',
    required: false,
    envKey: 'PAYPAL_CLIENT_ID'
  },
  {
    id: 'slack',
    name: 'Slack',
    category: 'Alerts',
    status: 'Not Connected',
    description: 'Internal system alerts for critical agent failures and compliance violations.',
    required: false,
    envKey: 'SLACK_WEBHOOK'
  },
];

const catColor: Record<string, string> = {
  'AI Engine': 'bg-purple-100 text-purple-700',
  'Billing':   'bg-green-100 text-green-700',
  'Email':     'bg-blue-100 text-blue-700',
  'SMS':       'bg-orange-100 text-orange-700',
  'Voice AI':  'bg-pink-100 text-pink-700',
  'Signatures':'bg-red-100 text-red-700',
  'Storage':   'bg-indigo-100 text-indigo-700',
  'Alerts':    'bg-yellow-100 text-yellow-700',
};
const statusColor: Record<string, string> = {
  'Connected':     'bg-green-100 text-green-700',
  'Not Connected': 'bg-gray-100 text-gray-500',
  'Error':         'bg-red-100 text-red-700',
};

const IntegrationHub: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>(INTEGRATIONS);
  const [selected, setSelected]         = useState<Integration | null>(null);
  const [apiKey, setApiKey]             = useState('');
  const [saved, setSaved]               = useState('');

  const connect = (id: string) => {
    if (!apiKey.trim()) return;
    setIntegrations(prev => prev.map(i => i.id === id ? { ...i, status: 'Connected' } : i));
    setSaved(id);
    setApiKey('');
    setSelected(null);
    setTimeout(() => setSaved(''), 3000);
  };

  const connected  = integrations.filter(i => i.status === 'Connected').length;
  const required   = integrations.filter(i => i.required && i.status !== 'Connected');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integration Hub</h1>
          <p className="text-sm text-gray-500 mt-1">
            {connected} of {integrations.length} services connected
          </p>
        </div>
        {saved && <span className="text-sm text-green-600 font-medium">✓ Connected</span>}
      </div>

      {required.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-sm font-medium text-yellow-800">
            ⚠ Required for full operation: {required.map(i => i.name).join(', ')}
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            API keys must be added to the <code className="bg-yellow-100 px-1 rounded">.env</code> file
            on the server by Amir. After updating .env, restart the backend:{' '}
            <code className="bg-yellow-100 px-1 rounded">docker restart lifewealth-backend</code>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {integrations.map(i => (
          <div key={i.id} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-900">{i.name}</p>
                  {i.required && (
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded font-medium">
                      Required
                    </span>
                  )}
                </div>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${catColor[i.category] || 'bg-gray-100 text-gray-600'}`}>
                  {i.category}
                </span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusColor[i.status]}`}>
                {i.status}
              </span>
            </div>

            <p className="text-xs text-gray-500">{i.description}</p>

            {i.setupNote && i.status !== 'Connected' && (
              <p className="text-xs text-blue-600 bg-blue-50 rounded p-2">{i.setupNote}</p>
            )}

            <div className="flex items-center gap-2 mt-auto">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 flex-1 truncate">
                {i.envKey}
              </code>
              {i.status !== 'Connected' && (
                <button
                  onClick={() => setSelected(i)}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-shrink-0"
                >
                  Configure
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-1">Configure {selected.name}</h2>
            <p className="text-xs text-gray-500 mb-2">
              This key is stored in .env by Amir on the server. Here you can verify the connection is saved.
            </p>
            {selected.setupNote && (
              <p className="text-xs text-blue-700 bg-blue-50 rounded p-2 mb-3">{selected.setupNote}</p>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {selected.envKey}
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder="Enter API key to test..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => connect(selected.id)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                Test &amp; Connect
              </button>
              <button
                onClick={() => { setSelected(null); setApiKey(''); }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationHub;
